/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';
import { IntlProvider } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';

const buttonContainer = css({
	position: 'absolute',
	transform: 'translateX(100%)',
	pointerEvents: 'all',
});

const SmallAIGenerativeTextIcon = () => <AiGenerativeTextIcon label="" size="small" />;

export function SuggestionIconButton({
	leftAdjustment,
	suggestion,
	suggestionsCount,
	isSelected,
	api,
	intl,
	topAdjustment,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	intl?: IntlShape;
	isSelected: boolean;
	leftAdjustment?: number;
	suggestion: Suggestion;
	suggestionsCount: number;
	// Used to visually adjust the position so it lines up with block controls
	topAdjustment?: number;
}): JSX.Element {
	const { baseLocalIdBySuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => {
			const { baseLocalIdBySuggestionId } = state?.aiSuggestionsState ?? {};

			return {
				baseLocalIdBySuggestionId,
			};
		},
	);

	const onSelectSuggestion = () => {
		api?.aiSuggestions?.actions.selectSuggestion(suggestion.id);
	};

	const tooltip = useMemo(
		() => ({
			content:
				suggestionsCount > 1
					? intl?.formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
							count: suggestionsCount,
						})
					: suggestion.shortDescription,
			position: 'right' as const,
		}),
		[intl, suggestion.shortDescription, suggestionsCount],
	);

	return (
		<IntlProvider
			defaultLocale={intl?.defaultLocale || 'en-US'}
			locale={intl?.locale || 'en-US'}
			messages={intl?.messages}
			formats={intl?.formats}
		>
			<span
				css={buttonContainer}
				style={{
					anchorName: `--button-${baseLocalIdBySuggestionId?.get(suggestion.id)}`,
					top: topAdjustment ?? 0,
					right: `calc(${token('space.negative.300')} + ${token('space.negative.300')} - ${
						leftAdjustment ?? 0
					}px)`,
				}}
			>
				{
					// We keep the anchorName span rendered so that the suggestion card can reference it
					!isSelected && (
						<IconButton
							isTooltipDisabled={false}
							tooltip={tooltip}
							icon={SmallAIGenerativeTextIcon}
							appearance="subtle"
							label=""
							spacing="compact"
							onClick={onSelectSuggestion}
							shape="circle"
						/>
					)
				}
			</span>
		</IntlProvider>
	);
}
